import {
  ConfigAction,
  ConfigContext,
  ConfigStateValue,
  initialConfigState,
} from "@contexts/ConfigContext";
import { ReactNode, useMemo, useReducer, useRef } from "react";

/**
 * Reducer function to manage the visibility state of various UI components.
 *
 * @param state - The current state of the configuration.
 * @param action - The action object containing the type and payload to update the state.
 * @returns The updated state based on the action type.
 *
 * Action Types:
 * - `"WIDGETS_VISIBILITY"`: Updates the visibility of the widgets panel. Closes all other panels.
 * - `"SETTINGS_VISIBILITY"`: Updates the visibility of the settings panel. Closes all other panels.
 * - `"SHARE_VISIBILITY"`: Updates the visibility of the share panel. Closes all other panels.
 * - `"DRAWER_VISIBILITY"`: Updates the visibility of the drawer panel. Closes all other panels.
 * - `"NOTIFICATIONS_VISIBILITY"`: Updates the visibility of the notifications panel. Closes all other panels.
 * - `"SEARCH_VISIBILITY"`: Updates the visibility of the search panel. Does not close other panels.
 * - `"MOBILE_SEARCH_VISIBILITY"`: Updates the visibility of the mobile search interface. Does not close other panels.
 * - `"SEARCH_INPUT_VALUE"`: Updates the search input value without affecting panel visibility.
 */
function configReducer(
  state: ConfigStateValue,
  action: ConfigAction
): ConfigStateValue {
  switch (action.type) {
    case "WIDGETS_VISIBILITY":
      return {
        ...state,
        isSettingsOpen: false,
        isWidgetsOpen: action.payload.visible,
        isShareOpen: false,
        isDrawerOpen: false,
        isNotificationsOpen: false,
        isSearchOpen: false,
      };
    case "SETTINGS_VISIBILITY":
      return {
        ...state,
        isSettingsOpen: action.payload.visible,
        isWidgetsOpen: false,
        isShareOpen: false,
        isDrawerOpen: false,
        isNotificationsOpen: false,
        isSearchOpen: false,
      };
    case "SHARE_VISIBILITY":
      return {
        ...state,
        isSettingsOpen: false,
        isWidgetsOpen: false,
        isShareOpen: action.payload.visible,
        isDrawerOpen: false,
        isNotificationsOpen: false,
        isSearchOpen: false,
      };
    case "DRAWER_VISIBILITY":
      return {
        ...state,
        isSettingsOpen: false,
        isWidgetsOpen: false,
        isShareOpen: false,
        isDrawerOpen: action.payload.visible,
        isNotificationsOpen: false,
        isSearchOpen: false,
      };
    case "NOTIFICATIONS_VISIBILITY":
      return {
        ...state,
        isSettingsOpen: false,
        isWidgetsOpen: false,
        isShareOpen: false,
        isDrawerOpen: false,
        isNotificationsOpen: action.payload.visible,
        isSearchOpen: false,
      };
    case "SEARCH_VISIBILITY":
      return {
        ...state,
        isSearchOpen: action.payload.visible,
      };
    case "MOBILE_SEARCH_VISIBILITY":
      return {
        ...state,
        isMobileSearchOpen: action.payload.visible,
      };
    case "SEARCH_INPUT_VALUE":
      return {
        ...state,
        searchInputValue: action.payload.value,
      };
    default:
      return state;
  }
}

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(configReducer, initialConfigState);
  const settingsRef = useRef<HTMLDivElement | null>(null);
  const widgetSwitchRef = useRef<HTMLDivElement | null>(null);
  const shareRef = useRef<HTMLDivElement | null>(null);
  const drawerSwitchRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      settingsRef,
      widgetSwitchRef,
      shareRef,
      drawerSwitchRef,
      notificationsRef,
      searchRef,
    }),
    [state, dispatch]
  );

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};
